import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Stack,
    Alert,
    MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import LinkIcon from '@mui/icons-material/Link';
import { axiosInstance } from '../../../api';

const CARD_SX = {
    p: 2.5,
    borderRadius: '16px',
    bgcolor: '#fff',
    border: '1px solid #eef0f4',
    boxShadow: '0 4px 20px rgba(17,24,39,0.05)',
};

const BiometricEmployeeMapping = () => {
    const [mappings, setMappings] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openModal, setOpenModal] = useState(false);
    const [alertInfo, setAlertInfo] = useState(null);

    const [formUserId, setFormUserId] = useState('');
    const [formEmployeeId, setFormEmployeeId] = useState('');
    const [formDeviceId, setFormDeviceId] = useState('');

    const fetchData = async () => {
        setLoading(true);
        try {
            const [mapRes, empRes, devRes] = await Promise.all([
                axiosInstance.get('/api/attendance/biometric/mappings/'),
                axiosInstance.get('/employees/'),
                axiosInstance.get('/api/attendance/biometric/devices/'),
            ]);
            setMappings(mapRes.data.mappings || []);
            setEmployees(empRes.data.results || empRes.data || []);
            setDevices(devRes.data.devices || []);
        } catch (err) {
            console.error('Error fetching mappings:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleCreateMapping = async () => {
        if (!formUserId.trim() || !formEmployeeId) return;
        try {
            const payload = {
                biometric_user_id: formUserId.trim(),
                employee_id: formEmployeeId,
                device_id: formDeviceId || null,
            };
            const res = await axiosInstance.post('/api/attendance/biometric/mappings/', payload);
            setAlertInfo({
                type: 'success',
                message: `Mapping saved! ${res.data.staged_punches_resolved || 0} existing staged punch(es) retroactively linked to employee.`,
            });
            setOpenModal(false);
            setFormUserId('');
            setFormEmployeeId('');
            setFormDeviceId('');
            fetchData();
        } catch (err) {
            setAlertInfo({ type: 'error', message: err.response?.data?.error || 'Failed to save mapping.' });
        }
    };

    return (
        <Box sx={{ mt: 2 }}>
            {alertInfo && (
                <Alert severity={alertInfo.type} sx={{ mb: 2, borderRadius: '12px' }} onClose={() => setAlertInfo(null)}>
                    {alertInfo.message}
                </Alert>
            )}

            <Paper elevation={0} sx={{ ...CARD_SX, mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#111827' }}>
                        Biometric User ID Mapping
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                        Link numeric biometric terminal user IDs to portal employee records.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1.5}>
                    <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchData} sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}>
                        Refresh
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setOpenModal(true)}
                        sx={{
                            borderRadius: '10px',
                            textTransform: 'none',
                            fontWeight: 700,
                            background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
                        }}
                    >
                        Map Employee
                    </Button>
                </Stack>
            </Paper>

            <Paper elevation={0} sx={{ ...CARD_SX, p: 0, overflow: 'hidden' }}>
                <TableContainer>
                    <Table>
                        <TableHead sx={{ bgcolor: '#f9fafb' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Biometric Terminal User ID</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Employee Code</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Employee Name</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Branch</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Device Scope</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Mapping Type</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Created Date</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                                        <CircularProgress size={36} />
                                    </TableCell>
                                </TableRow>
                            ) : mappings.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                                        <LinkIcon sx={{ fontSize: 40, color: '#9ca3af', mb: 1 }} />
                                        <Typography variant="body1" sx={{ fontWeight: 700 }}>No employee mappings configured</Typography>
                                        <Typography variant="caption" sx={{ color: '#6b7280' }}>
                                            Punches received from unmapped IDs will remain staged safely until mapped.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                mappings.map((m) => (
                                    <TableRow key={m.id} hover>
                                        <TableCell>
                                            <Chip label={m.biometric_user_id} size="small" sx={{ fontWeight: 800, bgcolor: '#eff6ff', color: '#1d4ed8' }} />
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>{m.employee_no}</TableCell>
                                        <TableCell>{m.employee_name}</TableCell>
                                        <TableCell>{m.branch_name || 'All Branches'}</TableCell>
                                        <TableCell>{m.device_name}</TableCell>
                                        <TableCell>
                                            <Chip label={m.mapping_method} size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.7rem' }} />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="text.secondary">
                                                {new Date(m.created_at).toLocaleDateString()}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Create Mapping Modal */}
            <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 800 }}>Map Biometric User ID to Employee</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ py: 1 }}>
                        <TextField
                            fullWidth
                            label="Biometric Device User ID (e.g. 101)"
                            size="small"
                            value={formUserId}
                            onChange={(e) => setFormUserId(e.target.value)}
                            sx={{ mb: 2.5 }}
                        />
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Select Portal Employee"
                            value={formEmployeeId}
                            onChange={(e) => setFormEmployeeId(e.target.value)}
                            sx={{ mb: 2.5 }}
                        >
                            {employees.map((emp) => (
                                <MenuItem key={emp.id} value={emp.id}>
                                    {emp.employee_no} — {emp.name || `${emp.first_name} ${emp.last_name}`} ({emp.branch_name || emp.branch || 'HO'})
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Specific Device (Optional)"
                            value={formDeviceId}
                            onChange={(e) => setFormDeviceId(e.target.value)}
                        >
                            <MenuItem value="">-- All Company Devices --</MenuItem>
                            {devices.map((d) => (
                                <MenuItem key={d.id} value={d.id}>
                                    {d.name} ({d.device_identifier})
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenModal(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateMapping}
                        disabled={!formUserId.trim() || !formEmployeeId}
                        sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700 }}
                    >
                        Save Mapping
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BiometricEmployeeMapping;

